const DESCRIPTION_LIMIT = 256;
const NOTES_LIMIT = 4000;
const PREVIEW_LENGTH = 50;
const SHOP_INVENTORY_VIEW = "shop-inventory";
const SHOP_INVENTORY_PRODUCTION_URL =
  "https://pixie.bigtapapp.com/mproperties?view=shop-inventory";

function shopInventoryViewFromQuery(query) {
  const raw = query && query.view;
  const view = Array.isArray(raw) ? raw[0] : raw;
  return view === SHOP_INVENTORY_VIEW ? SHOP_INVENTORY_VIEW : "";
}

function shopInventoryPreview(description) {
  const text = String(description || "").replace(/\s+/g, " ").trim();
  if (text.length <= PREVIEW_LENGTH) return text;
  return `${text.slice(0, PREVIEW_LENGTH).trimEnd()}…`;
}

function shopInventoryMatches(items, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return [];
  const list = Array.isArray(items) ? items : [];
  const matched = list.filter((item) =>
    String(item && item.description ? item.description : "")
      .toLowerCase()
      .includes(needle),
  );
  return matched.map((item) => ({
    ...item,
    preview: shopInventoryPreview(item && item.description),
  }));
}

function shopInventorySoleMatch(matches) {
  if (!Array.isArray(matches) || matches.length !== 1) return null;
  const only = matches[0];
  const id = Number(only && only.id);
  if (!Number.isInteger(id)) return null;
  return only;
}

function shopInventoryDefaultItem(items, query) {
  if (String(query || "").trim()) return null;
  const list = Array.isArray(items) ? items : [];
  return list.find((item) => Number(item && item.id) === 1) || null;
}

function shopPlaceCode(value) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 2);
}

const SHOP_DEFAULT_BUILDING = "315 Richard Terrace";
const SHOP_PLACE_MEMORY_KEY = "shop-inventory-last-place";

function shopPlaceMemory(value) {
  const source = value || {};
  return {
    buildingId: shopBuildingId(source.buildingId),
    rack: shopPlaceCode(source.rack),
    shelf: shopPlaceCode(source.shelf),
    box: shopPlaceCode(source.box),
  };
}

function readShopPlaceMemory(storage) {
  if (!storage || typeof storage.getItem !== "function") return null;
  try {
    const raw = storage.getItem(SHOP_PLACE_MEMORY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return shopPlaceMemory(parsed);
  } catch {
    return null;
  }
}

function writeShopPlaceMemory(storage, value) {
  const place = shopPlaceMemory(value);
  if (!storage || typeof storage.setItem !== "function") return place;
  try {
    storage.setItem(SHOP_PLACE_MEMORY_KEY, JSON.stringify(place));
  } catch {
    /* ignore quota or private mode */
  }
  return place;
}

function shopBuildingId(value) {
  const n = parseInt(String(value ?? "").trim(), 10);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

function shopDefaultBuildingId(buildings) {
  const match = (Array.isArray(buildings) ? buildings : []).find(
    (row) => String(row && row.label ? row.label : "").trim() === SHOP_DEFAULT_BUILDING,
  );
  return shopBuildingId(match && match.id);
}

function shopQty(value) {
  const n = parseInt(String(value ?? "").trim(), 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, 999999);
}

function shopText(value, limit) {
  return String(value ?? "").slice(0, limit);
}

function shopInventoryToday(now = new Date()) {
  return now.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

function shopInventoryDateLabel(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  return `${Number(match[2])}/${Number(match[3])}/${match[1].slice(2)}`;
}

const SHOP_SWIPE_MIN = 50;
const SHOP_SWIPE_MS = 800;

/** Property tools treat a window wider than 500px as desktop. */
const SHOP_DESKTOP_MIN_WIDTH = 501;

/**
 * Right arrow is the next item number. Left arrow is the previous.
 * Phone-width layouts keep swipe and ignore these keys.
 */
function shopInventoryArrowDirection(key, width) {
  if (!(Number(width) >= SHOP_DESKTOP_MIN_WIDTH)) return null;
  if (key === "ArrowRight") return "next";
  if (key === "ArrowLeft") return "prev";
  return null;
}

/** Left swipe is the next item number. Right swipe is the previous. */
function shopInventorySwipe(
  dx,
  dy,
  {
    minDistance = SHOP_SWIPE_MIN,
    maxDuration = SHOP_SWIPE_MS,
    elapsed = 0,
  } = {},
) {
  const absX = Math.abs(Number(dx) || 0);
  const absY = Math.abs(Number(dy) || 0);
  if (elapsed > maxDuration || absX < minDistance || absX < absY * 1.4) {
    return null;
  }
  return dx < 0 ? "next" : "prev";
}

function shopInventoryByNumber(items) {
  const ordered = [];
  const seen = new Set();
  for (const item of Array.isArray(items) ? items : []) {
    const id = Number(item && item.id);
    if (!Number.isInteger(id) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(item);
  }
  ordered.sort((a, b) => Number(a.id) - Number(b.id));
  return ordered;
}

/** Next or previous item by id. The ends wrap. */
function shopInventoryNeighbor(items, currentId, direction) {
  const ordered = shopInventoryByNumber(items);
  const count = ordered.length;
  if (!count) return null;
  const current = Number(currentId);
  let index = ordered.findIndex((item) => Number(item.id) === current);
  if (index < 0) {
    if (direction === "prev") {
      for (let i = count - 1; i >= 0; i -= 1) {
        if (Number(ordered[i].id) < current) return ordered[i];
      }
      return ordered[count - 1];
    }
    const after = ordered.find((item) => Number(item.id) > current);
    return after || ordered[0];
  }
  const step = direction === "prev" ? -1 : 1;
  return ordered[(((index + step) % count) + count) % count];
}

module.exports = {
  DESCRIPTION_LIMIT,
  NOTES_LIMIT,
  PREVIEW_LENGTH,
  SHOP_INVENTORY_VIEW,
  SHOP_INVENTORY_PRODUCTION_URL,
  shopInventoryViewFromQuery,
  shopInventoryPreview,
  shopInventoryMatches,
  shopInventorySoleMatch,
  shopInventoryDefaultItem,
  shopPlaceCode,
  shopBuildingId,
  shopDefaultBuildingId,
  SHOP_DEFAULT_BUILDING,
  SHOP_PLACE_MEMORY_KEY,
  readShopPlaceMemory,
  writeShopPlaceMemory,
  shopQty,
  shopText,
  shopInventoryToday,
  shopInventoryDateLabel,
  shopInventorySwipe,
  shopInventoryNeighbor,
  shopInventoryArrowDirection,
  SHOP_DESKTOP_MIN_WIDTH,
};
